import { Modal, QRCode } from "antd";
import { useTranslation } from "react-i18next";
import CommonStyles from "@components/CommonStyles";
import useToggle from "@base/hooks/useToggle";
import { IHealthInsuranceCard } from "@base/modules/patientInsurance/healthInsurance.interface";

/** `expired` carries both dates as `"<from> - <5 years from>"`; anything else is not shown. */
const splitExpired = (expired?: string) => {
  const parts = (expired ?? "").split("-");

  return parts.length === 2 ? { validFrom: parts[0].trim(), fiveYearsFrom: parts[1].trim() } : undefined;
};

interface IProps {
  card: IHealthInsuranceCard;
}

export default function HealthInsuranceCard({ card }: IProps) {
  //! state
  const { t } = useTranslation();
  const { open: openQr, toggle: toggleQr } = useToggle();
  const { open: openBenefits, toggle: toggleBenefits } = useToggle();

  //! function
  const expired = splitExpired(card.expired);
  const benefits = card.health_insurance_benefit?.description;

  const rows = [
    { label: t("user.profile.insurance.bhyt.cardNumber"), value: card.code },
    { label: t("user.profile.insurance.bhyt.dob"), value: card.dob },
    { label: t("user.profile.insurance.bhyt.gender"), value: card.gender },
    { label: t("user.profile.insurance.bhyt.address"), value: card.address },
    { label: t("user.profile.insurance.bhyt.code"), value: card.health_insurance?.code },
    { label: t("user.profile.insurance.bhyt.primaryCareFacility"), value: card.health_insurance?.name },
    { label: t("user.profile.insurance.bhyt.validFrom"), value: expired?.validFrom },
    { label: t("user.profile.insurance.bhyt.fiveYearsFrom"), value: expired?.fiveYearsFrom },
  ].filter((row) => !!row.value);

  //! render
  return (
    <div className="bg-common-background rounded-xl p-4 flex flex-col gap-3">
      <div className="text-common-maintext text-base font-semibold font-['Inter']">{t("user.profile.insurance.bhyt.title")}</div>

      <div className="flex flex-col sm:flex-row gap-4">
        {card.raw ? (
          <button type="button" className="self-center sm:self-start shrink-0 cursor-zoom-in" onClick={toggleQr}>
            <QRCode value={card.raw} size={120} bgColor="#ffffff" bordered={false} />
          </button>
        ) : null}

        <div className="flex flex-col gap-1 text-sm font-['Inter'] min-w-0">
          <div className="text-common-maintext font-bold uppercase break-words">{card.name}</div>

          {rows.map((row) => (
            <div key={row.label} className="break-words">
              <span className="text-common-subtext">{row.label}: </span>
              <span className="text-common-maintext">{row.value}</span>
            </div>
          ))}
        </div>
      </div>

      {benefits ? (
        <div>
          <CommonStyles.ButtonUI type="primary" ghost onClick={toggleBenefits}>
            {t("user.profile.insurance.bhyt.viewBenefits")}
          </CommonStyles.ButtonUI>
        </div>
      ) : null}

      <Modal open={openQr} onCancel={toggleQr} footer={null} centered width={360}>
        <div className="flex justify-center pt-6">{card.raw ? <QRCode value={card.raw} size={280} bgColor="#ffffff" bordered={false} /> : null}</div>
      </Modal>

      <Modal open={openBenefits} onCancel={toggleBenefits} footer={null} centered title={t("user.profile.insurance.bhyt.benefits")}>
        <div className="pt-2 text-common-maintext text-sm font-['Inter'] whitespace-pre-line">{benefits}</div>
      </Modal>
    </div>
  );
}
